import getSelectedMediaElementsObjects from 'editor/src/store/design/selector/getSelectedMediaElementsObjects';
import { MediaImage } from 'editor/src/store/design/types';
import { getImageOpacity, toStoredImageOpacity } from 'editor/src/store/design/util/imageOpacity';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { logEvent } from 'editor/src/amplitude';

import applyToSelectedMediaTypeOperation from './applyToSelectedMediaTypeOperation';

/**
 * GAPI-61889: sets the opacity (0–1) of every selected image as a single undo step. Fully opaque removes the key.
 * Does nothing (and records no undo step) when no selected image would change.
 */
const applyImageOpacityToSelectionOperation =
  (opacity: number) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const storedOpacity = toStoredImageOpacity(opacity);
    const targetOpacity = storedOpacity ?? 1;
    const selectedImages = getSelectedMediaElementsObjects(getState()).filter(
      (element): element is MediaImage => element.type === 'image',
    );
    // a change is a different effective opacity, or a stored key to drop (e.g. a legacy explicit `opacity: 1`)
    const needsUpdate = (element: MediaImage) =>
      getImageOpacity(element) !== targetOpacity || (storedOpacity === undefined && element.opacity !== undefined);
    if (!selectedImages.some(needsUpdate)) {
      return;
    }

    logEvent('element modified', { type: 'image', property: 'opacity', value: targetOpacity });
    dispatch(
      applyToSelectedMediaTypeOperation('image', function imageOpacity(element: MediaImage) {
        return element.opacity === storedOpacity ? undefined : { opacity: storedOpacity };
      }),
    );
  };

export default applyImageOpacityToSelectionOperation;
